<launch-token-tester-modal>
    <!--
        Launch Token Tester (SYSTEM_ADMIN only).

        Pre-dates the cc-modal design vocabulary that Customer 360 uses
        now. This template was restyled to match (cc-modal / cc-modal-
        header / cc-modal-body / cc-modal-footer, cc-field / cc-label,
        unified close glyph) so the tool feels like a peer of the other
        admin modals on the API, Settings, and Billing tabs. The outer
        `.modal.modal-open` pattern stays — existing callers drive
        open/close through `props.isOpen`, and switching to a <dialog>
        element would force a contract change and a caller rewrite.
    -->
    <div class="modal {props.isOpen ? 'modal-open' : ''}">
        <div class="modal-box cc-modal p-0 max-w-2xl">
            <div class="cc-modal-header flex items-center gap-3">
                <div class="cc-modal-icon cc-modal-icon-info">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                </div>
                <div class="flex-1 min-w-0">
                    <h3 class="font-semibold text-base leading-tight">{locale?.t('launchtoken_title')}</h3>
                    <p class="text-xs text-base-content/80 mt-0.5">
                        {state.tokenType === 'insights'
                            ? locale?.t('launchtoken_desc_insights')
                            : locale?.t('launchtoken_desc_player')}
                    </p>
                </div>
                <button class="btn btn-sm btn-ghost btn-square -me-1" onclick="{() => onClose()}" aria-label="{locale?.t('close')}">
                    <i class="icon-metalark-x"></i>
                </button>
            </div>

            <!-- Pre-generation form ─────────────────────────────── -->
            <div if="{!state.generatedToken}">
                <div class="cc-modal-body space-y-4">
                    <!-- Token type segmented control. Player tokens bind to a
                         single resource and launch /player; insights tokens
                         are slice-scoped and launch /insights. -->
                    <div class="cc-field">
                        <label class="cc-label">{locale?.t('launchtoken_tokentype')}</label>
                        <div class="join">
                            <button type="button"
                                class="btn btn-sm join-item {state.tokenType === 'player' ? 'btn-primary' : 'bg-white border border-base-300'}"
                                onclick="{() => setTokenType('player')}">
                                {locale?.t('launchtoken_player')}
                            </button>
                            <button type="button"
                                class="btn btn-sm join-item {state.tokenType === 'insights' ? 'btn-primary' : 'bg-white border border-base-300'}"
                                onclick="{() => setTokenType('insights')}">
                                {locale?.t('launchtoken_insights')}
                            </button>
                        </div>
                    </div>

                    <!-- Random-test-data info banner -->
                    <div class="cc-audit-banner" style="background-color: color-mix(in oklch, var(--color-info) 10%, transparent); border-inline-start-color: color-mix(in oklch, var(--color-info) 60%, transparent);">
                        <i class="icon-metalark-info text-info flex-shrink-0 mt-0.5"></i>
                        <span>{locale?.t('launchtoken_randomdata_info')}</span>
                    </div>

                    <!-- Resource ID with search (player tokens only — insights
                         tokens are slice-wide and don't bind to a resource) -->
                    <div class="cc-field" if="{state.tokenType !== 'insights'}">
                        <label class="cc-label">
                            {locale?.t('launchtoken_resource')} <span class="cc-required">*</span>
                            <span class="ms-auto text-[11px] text-base-content/55" if="{state.resourcesLoading}">{locale?.t('launchtoken_loadingresources')}</span>
                        </label>

                        <!-- Selected resource chip -->
                        <div if="{state.resourceId}" class="mb-2">
                            <div class="bg-primary text-primary-content rounded-lg px-3 py-2 flex items-start gap-2">
                                <div class="flex-1 min-w-0">
                                    <div class="font-semibold break-words whitespace-normal text-sm">{state.resourceDisplayName}</div>
                                    <div class="text-xs opacity-75 font-mono mt-1 break-all">{state.resourceId}</div>
                                </div>
                                <button type="button"
                                    class="btn btn-ghost btn-xs rounded-full flex-shrink-0 mt-1"
                                    onclick="{clearResourceSelection}"
                                    title="{locale?.t('clearselection')}"
                                    aria-label="{locale?.t('clearselection')}">
                                    <i class="icon-metalark-x"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Search input -->
                        <div class="relative">
                            <input id="launch-token-tester-modal-resource-search"
                                type="text"
                                placeholder="{locale?.t('launchtoken_searchresources')}"
                                aria-label="{locale?.t('launchtoken_searchresources')}"
                                class="input input-bordered w-full"
                                value="{state.resourceSearch}"
                                oninput="{handleResourceSearch}"
                                onfocus="{() => { state.showResourceDropdown = true; update(); }}"
                                onblur="{closeResourceDropdownWithDelay}" />

                            <!-- Results dropdown -->
                            <div class="absolute top-full start-0 end-0 z-10 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-40 overflow-y-auto"
                                if="{state.showResourceDropdown && !state.resourcesLoading}">
                                <div if="{state.resources.length === 0}" class="p-3 text-sm text-base-content/80 text-center">
                                    <div class="mb-1">{locale?.t('launchtoken_noresources')}</div>
                                    <div class="text-xs text-base-content/45">{locale?.t('launchtoken_noresources_hint')}</div>
                                </div>
                                <div if="{state.resources.length > 0 && state.searchResults.length === 0}" class="p-3 text-sm text-base-content/80 text-center">
                                    {locale?.t('launchtoken_nomatching')}
                                </div>
                                <button type="button" each="{resource in state.searchResults}"
                                    class="p-3 hover:bg-base-200 cursor-pointer border-b border-base-200 last:border-b-0"
                                    onclick="{(e) => selectResourceItem(resource)}">
                                    <div class="font-medium text-sm">{resource.displayName || locale?.t('untitled')}</div>
                                    <div class="text-xs text-base-content/80 font-mono">{resource.resourceId || 'N/A'}</div>
                                </button>
                            </div>

                            <!-- Loading indicator in dropdown -->
                            <div class="absolute top-full start-0 end-0 z-10 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-3 text-center text-sm text-base-content/80"
                                if="{state.showResourceDropdown && state.resourcesLoading}">
                                <span class="loading loading-spinner loading-sm me-2"></span>
                                {locale?.t('launchtoken_loadingresources')}
                            </div>
                        </div>
                    </div>

                    <!-- End-user metadata (email / id / login / name) in a
                         2-column grid so the modal feels denser and more
                         like the other admin surfaces. -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div class="cc-field">
                            <label class="cc-label" for="launch-token-tester-modal-enduser-email">{locale?.t('launchtoken_enduseremail')}</label>
                            <input id="launch-token-tester-modal-enduser-email"
                                type="email"
                                placeholder="student@example.com"
                                class="input input-bordered w-full"
                                value="{state.endUserEmail}"
                                oninput="{handleEmailChange}" />
                        </div>
                        <div class="cc-field">
                            <label class="cc-label" for="launch-token-tester-modal-enduser-id">{locale?.t('launchtoken_enduserid')}</label>
                            <input id="launch-token-tester-modal-enduser-id"
                                type="text"
                                placeholder="student_123"
                                class="input input-bordered w-full"
                                value="{state.endUserId}"
                                oninput="{handleUserIdChange}" />
                        </div>
                        <div class="cc-field">
                            <label class="cc-label" for="launch-token-tester-modal-enduser-login">{locale?.t('launchtoken_enduserlogin')}</label>
                            <input id="launch-token-tester-modal-enduser-login"
                                type="text"
                                placeholder="jdoe"
                                class="input input-bordered w-full"
                                value="{state.endUserLogin}"
                                oninput="{handleLoginChange}" />
                        </div>
                        <div class="cc-field">
                            <label class="cc-label" for="launch-token-tester-modal-enduser-name">{locale?.t('launchtoken_endusername')}</label>
                            <input id="launch-token-tester-modal-enduser-name"
                                type="text"
                                placeholder="John Doe"
                                class="input input-bordered w-full"
                                value="{state.endUserName}"
                                oninput="{handleNameChange}" />
                        </div>
                    </div>

                    <!-- Expiration slider -->
                    <div class="cc-field">
                        <label class="cc-label" for="launch-token-tester-modal-expiration">
                            {locale?.t('launchtoken_expires_minutes')}
                            <span class="ms-auto font-mono font-semibold text-accent">{state.expirationMinutes}</span>
                        </label>
                        <input id="launch-token-tester-modal-expiration"
                            type="range"
                            min="5"
                            max="60"
                            class="range range-sm"
                            value="{state.expirationMinutes}"
                            oninput="{handleExpirationChange}" />
                        <div class="flex justify-between text-[11px] text-base-content/55 mt-1">
                            <span>{locale?.t('launchtoken_min_short', { count: 5 })}</span>
                            <span>{locale?.t('launchtoken_min_short', { count: 60 })}</span>
                        </div>
                    </div>

                    <!-- Error surface -->
                    <div class="alert alert-error py-2 text-xs" if="{state.error}">
                        <i class="icon-metalark-alert-circle"></i>
                        <span>{state.error}</span>
                    </div>
                </div>

                <div class="cc-modal-footer">
                    <button class="btn btn-sm btn-ghost" onclick="{regenerateTestData}">
                        <i class="icon-metalark-refresh me-1"></i>
                        {locale?.t('launchtoken_randomdata')}
                    </button>
                    <button class="btn btn-sm btn-primary"
                        onclick="{generateToken}"
                        disabled="{state.loading || (state.tokenType !== 'insights' && !state.resourceId)}">
                        <span class="loading loading-spinner loading-sm" if="{state.loading}"></span>
                        {state.loading ? locale?.t('launchtoken_generating') : locale?.t('launchtoken_generatetoken')}
                    </button>
                </div>
            </div>

            <!-- Post-generation result view ───────────────────── -->
            <div if="{state.generatedToken}">
                <div class="cc-modal-body space-y-4">
                    <div class="alert alert-success py-2 text-sm">
                        <i class="icon-metalark-check-circle"></i>
                        <span>{state.tokenType === 'insights' ? locale?.t('launchtoken_insights_generated') : locale?.t('launchtoken_player_generated')}</span>
                    </div>

                    <!-- Token value. Password-masked by default; toggle
                         reveals it. Copy sits on the same row for
                         parity with the token-generate modal on the
                         API tab. -->
                    <div class="cc-field">
                        <label class="cc-label" for="launch-token-tester-modal-generated-token">{locale?.t('launchtoken_token_label')}</label>
                        <div class="flex gap-2">
                            <input id="launch-token-tester-modal-generated-token"
                                type="{state.showToken ? 'text' : 'password'}"
                                class="input input-bordered flex-1 font-mono text-xs"
                                value="{state.generatedToken}"
                                readonly />
                            <button class="btn btn-square btn-ghost"
                                onclick="{toggleTokenVisibility}"
                                title="{state.showToken ? locale?.t('launchtoken_hidetoken') : locale?.t('launchtoken_showtoken')}"
                                aria-label="{state.showToken ? locale?.t('launchtoken_hidetoken') : locale?.t('launchtoken_showtoken')}">
                                <i class="icon-metalark-{state.showToken ? 'eye-off' : 'eye'}"></i>
                            </button>
                            <button class="btn btn-square btn-ghost"
                                onclick="{copyTokenToClipboard}"
                                title="{locale?.t('launchtoken_copytoken')}"
                                aria-label="{locale?.t('launchtoken_copytoken')}">
                                <i class="icon-metalark-{state.tokenCopied ? 'check' : 'copy'}"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Launch URL with copy -->
                    <div class="cc-field">
                        <label class="cc-label" for="launch-token-tester-modal-launch-url">{locale?.t('launchtoken_launchurl')}</label>
                        <div class="flex gap-2">
                            <input id="launch-token-tester-modal-launch-url"
                                type="text"
                                class="input input-bordered flex-1 font-mono text-xs"
                                value="{state.launchUrl}"
                                readonly />
                            <button class="btn btn-square btn-ghost"
                                onclick="{copyUrlToClipboard}"
                                title="{locale?.t('launchtoken_copyurl')}"
                                aria-label="{locale?.t('launchtoken_copyurl')}">
                                <i class="icon-metalark-{state.urlCopied ? 'check' : 'copy'}"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Token meta — mirrors the detail drawer grid
                         from the API tab so the visual vocabulary stays
                         consistent across token surfaces. Right column shows
                         the resource for player tokens, the slice for
                         insights tokens. -->
                    <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm rounded-md bg-base-200/40 p-3 border border-base-200">
                        <div>
                            <div class="text-[10px] uppercase tracking-wide text-base-content/80">{locale?.t('launchtoken_expiresat')}</div>
                            <div class="font-medium">{tools?.formatDateTime(state.expiresAt)}</div>
                        </div>
                        <div class="min-w-0" if="{state.tokenType !== 'insights'}">
                            <div class="text-[10px] uppercase tracking-wide text-base-content/80">{locale?.t('resourceid')}</div>
                            <div class="font-mono text-xs break-all">{state.resourceId}</div>
                        </div>
                        <div class="min-w-0" if="{state.tokenType === 'insights'}">
                            <div class="text-[10px] uppercase tracking-wide text-base-content/80">{locale?.t('launchtoken_sliceid')}</div>
                            <div class="font-mono text-xs break-all">{state.generatedSliceId}</div>
                        </div>
                    </div>
                </div>

                <div class="cc-modal-footer">
                    <button class="btn btn-sm btn-ghost" onclick="{reset}">
                        {locale?.t('launchtoken_generateanother')}
                    </button>
                    <a href="{state.launchUrl}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-success">
                        <i class="icon-metalark-open-in-new me-1"></i>
                        {state.tokenType === 'insights' ? locale?.t('launchtoken_launch_insights') : locale?.t('launchtoken_launch_player')}
                    </a>
                </div>
            </div>
        </div>
    </div>

    <script>
        import { getApi, getResourceService } from '../../services/service-loader';

        export default {
            state: {
                // METALARK-766: 'player' (default, single-resource) or 'insights' (slice-wide dashboard)
                tokenType: 'player',
                endUserEmail: '',
                endUserId: '',
                endUserLogin: '',
                endUserName: '',
                expirationMinutes: 30,
                loading: false,
                error: '',
                generatedToken: '',
                launchUrl: '',
                expiresAt: null,
                generatedSliceId: '', // populated by insights mint response
                showToken: false,
                tokenCopied: false,
                urlCopied: false,
                _wasOpen: false,
                // Resource search state
                resources: [],
                resourceSearch: '',
                resourceDisplayName: '',
                searchResults: [],
                showResourceDropdown: false,
                resourcesLoading: false
            },

            onMounted() {
                console.log('[LaunchTokenTesterModal] Component mounted, props:', this.props);
                // Pre-populate with random test data
                this.regenerateTestData();
            },

            onBeforeUpdate() {
                console.log('[LaunchTokenTesterModal] Before update, props.isOpen:', this.props?.isOpen, 'state.generatedToken:', this.state.generatedToken);

                // When modal is opened (only once per open)
                if (this.props?.isOpen && !this.state._wasOpen && !this.state.generatedToken) {
                    // Set flag BEFORE regenerating to prevent infinite loop
                    this.state._wasOpen = true;
                    // Generate cohesive random end user data
                    const userData = this.generateCoherentUserData();
                    this.state.endUserName = userData.name;
                    this.state.endUserEmail = userData.email;
                    this.state.endUserLogin = userData.login;
                    this.state.endUserId = `user_${this.generateRandomId()}`;
                    // Load resources for search (preserve resource selection from previous use)
                    this.loadResources();
                }

                // Track whether modal was previously open
                if (!this.props?.isOpen && this.state._wasOpen) {
                    this.state._wasOpen = false;
                    this.state.showResourceDropdown = false;
                }
            },

            async loadResources() {
                try {
                    console.log('[LaunchTokenTesterModal] Starting to load resources...');
                    this.state.resourcesLoading = true;
                    this.update();

                    const api = await getApi();
                    console.log('[LaunchTokenTesterModal] Making API call to /resources');

                    // Fetch resources directly from API
                    const response = await api.get('/resources', {
                        params: {
                            page: 1,
                            limit: 100
                        }
                    });

                    console.log('[LaunchTokenTesterModal] API response:', response.status, response.data);

                    // Handle both direct array and paginated response formats
                    let resources = [];
                    if (Array.isArray(response.data)) {
                        resources = response.data;
                        console.log('[LaunchTokenTesterModal] Response is direct array');
                    } else if (response.data?.data && Array.isArray(response.data.data)) {
                        resources = response.data.data;
                        console.log('[LaunchTokenTesterModal] Response has .data array');
                    } else if (response.data?.resources && Array.isArray(response.data.resources)) {
                        resources = response.data.resources;
                        console.log('[LaunchTokenTesterModal] Response has .resources array');
                    } else {
                        console.warn('[LaunchTokenTesterModal] Unexpected response format:', Object.keys(response.data));
                    }

                    this.state.resources = resources;
                    this.state.searchResults = this.state.resources.slice(0, 10); // Show first 10 by default
                    console.log('[LaunchTokenTesterModal] Loaded resources:', this.state.resources.length);
                    if (this.state.resources.length > 0) {
                        console.log('[LaunchTokenTesterModal] Sample resources:', this.state.resources.slice(0, 2).map(r => ({ displayName: r.displayName, resourceId: r.resourceId })));
                    }
                } catch (error) {
                    console.error('[LaunchTokenTesterModal] Failed to load resources:', error.message);
                    console.error('[LaunchTokenTesterModal] Error details:', error);
                    this.state.resources = [];
                    this.state.searchResults = [];
                } finally {
                    this.state.resourcesLoading = false;
                    this.update();
                }
            },

            handleResourceSearch(e) {
                this.state.resourceSearch = e.target.value;
                this.state.showResourceDropdown = true;

                console.log('[LaunchTokenTesterModal] Search input changed:', this.state.resourceSearch);
                console.log('[LaunchTokenTesterModal] Available resources:', this.state.resources.length);

                if (this.state.resourceSearch.trim() === '') {
                    // Show first 10 resources if search is empty
                    this.state.searchResults = this.state.resources.slice(0, 10);
                    console.log('[LaunchTokenTesterModal] Showing first 10 resources');
                } else {
                    // Filter resources by displayName or resourceId
                    const query = this.state.resourceSearch.toLowerCase();
                    this.state.searchResults = this.state.resources.filter(r => {
                        const displayNameMatch = (r.displayName && r.displayName.toLowerCase().includes(query));
                        const resourceIdMatch = (r.resourceId && r.resourceId.toLowerCase().includes(query));
                        const matches = displayNameMatch || resourceIdMatch;
                        if (matches && this.state.searchResults.length < 3) {
                            console.log('[LaunchTokenTesterModal] Match:', r.displayName, '|', r.resourceId);
                        }
                        return matches;
                    }).slice(0, 10);
                    console.log('[LaunchTokenTesterModal] Found', this.state.searchResults.length, 'matching resources for query:', query);
                }
                this.update();
            },

            selectResourceItem(resource) {
                console.log('[LaunchTokenTesterModal] Resource item clicked:', resource);
                console.log('[LaunchTokenTesterModal] Resource properties - resourceId:', resource?.resourceId, 'displayName:', resource?.displayName);
                console.log('[LaunchTokenTesterModal] Full resource object:', JSON.stringify(resource));
                if (resource && resource.resourceId) {
                    this.selectResource(resource);
                } else {
                    console.error('[LaunchTokenTesterModal] Resource is invalid:', resource);
                }
            },

            selectResource(resource) {
                console.log('[LaunchTokenTesterModal] Selecting resource:', resource);
                const resourceId = resource.resourceId || resource.id;
                const resourceDisplay = resource.displayName || 'Untitled';

                console.log('[LaunchTokenTesterModal] Before update - resourceId:', this.state.resourceId);
                this.state.resourceId = resourceId;
                this.state.resourceDisplayName = resourceDisplay;
                this.state.resourceSearch = '';  // Clear the input field
                this.state.showResourceDropdown = false;
                this.state.error = '';
                console.log('[LaunchTokenTesterModal] After state set - resourceId:', this.state.resourceId, 'displayName:', this.state.resourceDisplayName);
                this.update();
                console.log('[LaunchTokenTesterModal] After update - resourceId:', this.state.resourceId, 'displayName:', this.state.resourceDisplayName);
            },

            clearResourceSelection() {
                console.log('[LaunchTokenTesterModal] Clearing resource selection');
                this.state.resourceId = '';
                this.state.resourceDisplayName = '';
                this.state.resourceSearch = '';
                this.state.showResourceDropdown = false;
                this.state.error = '';
                this.update();
            },

            closeResourceDropdown() {
                this.state.showResourceDropdown = false;
                this.update();
            },

            closeResourceDropdownWithDelay() {
                setTimeout(() => {
                    this.state.showResourceDropdown = false;
                    this.update();
                }, 200);
            },

            handleResourceIdChange(e) {
                this.state.resourceId = e.target.value;
                this.state.error = '';
                this.update();
            },

            handleEmailChange(e) {
                this.state.endUserEmail = e.target.value;
                this.state.error = '';
                this.update();
            },

            handleUserIdChange(e) {
                this.state.endUserId = e.target.value;
                this.state.error = '';
                this.update();
            },

            handleLoginChange(e) {
                this.state.endUserLogin = e.target.value;
                this.state.error = '';
                this.update();
            },

            handleNameChange(e) {
                this.state.endUserName = e.target.value;
                this.state.error = '';
                this.update();
            },

            handleExpirationChange(e) {
                this.state.expirationMinutes = parseInt(e.target.value) || 30;
                this.state.error = '';
                this.update();
            },

            generateRandomId() {
                return Math.random().toString(36).substring(2, 10);
            },

            generateCoherentUserData() {
                // Generate a consistent first and last name
                const firstNames = ['John', 'Jane', 'Alex', 'Sarah', 'Michael', 'Emily', 'David', 'Jessica', 'Robert', 'Maria'];
                const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Anderson'];

                const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
                const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];

                // Generate all fields based on the same name
                const fullName = `${firstName} ${lastName}`;
                const emailFormat = Math.floor(Math.random() * 3); // Random email format
                let email;
                switch(emailFormat) {
                    case 0: // first.last@example.com
                        email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;
                        break;
                    case 1: // firstlast@example.com
                        email = `${firstName.toLowerCase()}${lastName.toLowerCase()}@example.com`;
                        break;
                    case 2: // first@example.com
                        email = `${firstName.toLowerCase()}@example.com`;
                        break;
                }

                // Generate login from name (e.g., jsmith, john.smith, or j.smith)
                const loginFormat = Math.floor(Math.random() * 3);
                let login;
                switch(loginFormat) {
                    case 0: // firstnamelastname
                        login = `${firstName.toLowerCase()}${lastName.toLowerCase()}`;
                        break;
                    case 1: // first.last
                        login = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`;
                        break;
                    case 2: // f.last or first.l
                        login = `${firstName.charAt(0).toLowerCase()}.${lastName.toLowerCase()}`;
                        break;
                }

                return {
                    name: fullName,
                    email: email,
                    login: login
                };
            },

            generateRandomEmail() {
                // This is now handled by generateCoherentUserData
                return '';
            },

            generateRandomLogin() {
                // This is now handled by generateCoherentUserData
                return '';
            },

            generateRandomName() {
                // This is now handled by generateCoherentUserData
                return '';
            },

            regenerateTestData() {
                // Generate cohesive, professional test user data
                const userData = this.generateCoherentUserData();
                this.state.endUserName = userData.name;
                this.state.endUserEmail = userData.email;
                this.state.endUserLogin = userData.login;
                this.state.endUserId = `user_${this.generateRandomId()}`;
                this.state.expirationMinutes = 30;
                this.update();
            },

            setTokenType(type) {
                if (type !== 'player' && type !== 'insights') return;
                this.state.tokenType = type;
                this.state.error = '';
                // Clear any post-result state when toggling between modes
                // pre-generation so the form stays clean.
                if (!this.state.generatedToken) {
                    if (type === 'insights') {
                        // Insights doesn't use a resource — drop the picker selection
                        // so the disabled-state on Generate flips correctly.
                        this.state.resourceId = '';
                        this.state.resourceDisplayName = '';
                        this.state.resourceSearch = '';
                        this.state.showResourceDropdown = false;
                    }
                }
                this.update();
            },

            async generateToken() {
                this.state.loading = true;
                this.state.error = '';

                try {
                    const api = await getApi();
                    const endUser = {
                        email: this.state.endUserEmail || undefined,
                        id: this.state.endUserId || undefined,
                        login: this.state.endUserLogin || undefined,
                        name: this.state.endUserName || undefined
                    };

                    let response;
                    if (this.state.tokenType === 'insights') {
                        // METALARK-766: slice-scoped, no resource binding. The
                        // backend reads sliceId/customerId from QueryContext,
                        // so the active session (regular, impersonation, or
                        // workspace-access) determines the scope.
                        response = await api.post('/launch-tokens/insights', {
                            expirationMinutes: this.state.expirationMinutes,
                            endUser
                        });
                        this.state.generatedSliceId = response.data.sliceId || '';
                    } else {
                        response = await api.post('/launch-tokens', {
                            resourceId: this.state.resourceId,
                            expirationMinutes: this.state.expirationMinutes,
                            endUser
                        });
                        this.state.generatedSliceId = '';
                    }

                    this.state.generatedToken = response.data.token;
                    this.state.launchUrl = response.data.launchUrl;
                    this.state.expiresAt = new Date(response.data.expiresAt);
                    this.state.showToken = false;
                    this.state.tokenCopied = false;
                    this.state.urlCopied = false;
                } catch (error) {
                    this.state.error = error.response?.data?.message || error.message || 'Failed to generate token';
                    console.error('Launch token generation failed:', error);
                }

                this.state.loading = false;
                this.update();
            },

            toggleTokenVisibility() {
                this.state.showToken = !this.state.showToken;
                this.update();
            },

            copyTokenToClipboard() {
                navigator.clipboard.writeText(this.state.generatedToken);
                this.state.tokenCopied = true;
                this.update();
                setTimeout(() => {
                    this.state.tokenCopied = false;
                    this.update();
                }, 2000);
            },

            copyUrlToClipboard() {
                navigator.clipboard.writeText(this.state.launchUrl);
                this.state.urlCopied = true;
                this.update();
                setTimeout(() => {
                    this.state.urlCopied = false;
                    this.update();
                }, 2000);
            },

            reset() {
                this.state.generatedToken = '';
                this.state.launchUrl = '';
                this.state.expiresAt = null;
                this.state.generatedSliceId = '';
                this.state.endUserEmail = '';
                this.state.endUserId = '';
                this.state.endUserLogin = '';
                this.state.endUserName = '';
                this.state.expirationMinutes = 30;
                this.state.error = '';
                this.regenerateTestData();
            },

            onClose() {
                if (this.props.onClose) {
                    this.props.onClose();
                }
            },

        };
    </script>

    <style>
        /* cc-modal design language — mirrors the declarations in
         * customer-360.tag / customer-api-tab.tag / customer-billing-
         * admin-actions.tag. Duplicated here because Riot scopes styles
         * per tag: this component needs to own its look even when
         * mounted outside Customer 360 (e.g. the legacy admin page). */
        .cc-modal {
            border-radius: 14px;
            box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.18),
                        0 4px 8px -2px rgba(0, 0, 0, 0.04);
        }
        .cc-modal-header {
            padding: 1.125rem 1.25rem;
            border-bottom: 1px solid var(--color-base-200);
        }
        .cc-modal-body { padding: 1.25rem; }
        .cc-modal-footer {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5rem;
            padding: 0.875rem 1.25rem;
            background-color: color-mix(in oklch, var(--color-base-200) 40%, transparent);
            border-top: 1px solid var(--color-base-200);
        }
        .cc-modal-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            flex-shrink: 0;
        }
        .cc-modal-icon-info {
            background-color: color-mix(in oklch, var(--color-info) 15%, transparent);
            color: var(--color-info);
        }
        .cc-field {
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
        }
        .cc-label {
            font-size: 0.8125rem;
            font-weight: 500;
            color: var(--color-base-content);
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            letter-spacing: 0.01em;
        }
        .cc-required {
            color: var(--color-error);
            font-weight: 600;
        }
        .cc-audit-banner {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            padding: 0.625rem 0.75rem;
            background-color: color-mix(in oklch, var(--color-warning) 8%, transparent);
            border-inline-start: 3px solid color-mix(in oklch, var(--color-warning) 60%, transparent);
            border-radius: 4px;
            font-size: 0.75rem;
            color: color-mix(in oklch, var(--color-base-content) 80%, transparent);
            line-height: 1.5;
        }
    </style>
</launch-token-tester-modal>
